<template>
  <header class="app-header">
    <div class="header-container">
      <div class="header-left">
        <button
          class="collapse-btn"
          :title="collapsed ? '展开菜单' : '收起菜单'"
          @click="emitToggleSidebar"
        >
          <el-icon>
            <Expand v-if="collapsed" />
            <Fold v-else />
          </el-icon>
        </button>

        <el-breadcrumb v-if="breadcrumbs.length" separator="/" class="header-breadcrumb">
          <el-breadcrumb-item
            v-for="(item, index) in breadcrumbs"
            :key="`${item}-${index}`"
          >
            <span class="crumb-text" :class="{ current: index === breadcrumbs.length - 1 }">
              {{ item }}
            </span>
          </el-breadcrumb-item>
        </el-breadcrumb>
      </div>

      <div class="header-right">
        <el-dropdown trigger="click" @command="handleUserCommand">
          <div class="user-info">
            <span class="user-name">{{ currentUserMeta.label }}</span>
            <span class="dropdown-arrow">▼</span>
            <span class="user-avatar">{{ currentUserMeta.avatar }}</span>
          </div>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
  </header>
</template>

<script>
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getEntryByProtectedPath,
  getLoginEntry,
  getLoginMetaByEntry,
  getLoginPathByEntry,
  removeTokenByEntry,
  setLoginEntry
} from '@/utils/loginEntry'

const BREADCRUMB_MAP = {
  '/report/reportCenter': ['举报中心', '我的举报'],
  '/admin/platformAccount': ['账号管理', '平台账号'],
  '/admin/manageAccount': ['账号管理', '账号列表'],
  '/verify/verifyCenter': ['核证中心', '核证工作台'],
  '/admin/productList': ['产品知识库', '产品列表'],
  '/admin/dictionaryList': ['字典管理', '字典列表'],
  '/admin/clueList': ['线索管理', '线索列表'],
  '/admin/clueGroupList': ['线索管理', '线索分组']
}

export default {
  name: 'PageHeader',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-sidebar'],
  setup(props, { emit }) {
    const router = useRouter()
    const route = useRoute()

    const currentEntry = computed(() => getEntryByProtectedPath(route.path) || getLoginEntry())
    const currentUserMeta = computed(() => getLoginMetaByEntry(currentEntry.value))

    const breadcrumbs = computed(() => {
      if (BREADCRUMB_MAP[route.path]) {
        return BREADCRUMB_MAP[route.path]
      }

      return route.name ? [String(route.name)] : []
    })

    const navigateToLogin = () => {
      setLoginEntry(currentEntry.value)
      router.push(getLoginPathByEntry(currentEntry.value))
    }

    const handleUserCommand = async (command) => {
      if (command !== 'logout') {
        return
      }

      try {
        await ElMessageBox.confirm('确认退出当前账号吗？', '退出登录', {
          type: 'warning',
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        })

        removeTokenByEntry(currentEntry.value)
        ElMessage.success('已退出登录')
        navigateToLogin()
      } catch (error) {
        // 用户取消退出时不提示
      }
    }

    return {
      emitToggleSidebar: () => emit('toggle-sidebar'),
      breadcrumbs,
      currentUserMeta,
      handleUserCommand
    }
  }
}
</script>

<style lang="scss" scoped>
.app-header {
  width: 100%;
  margin: 0;
  padding: 0;
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: #ffffff;
  border-bottom: 1px solid #e8edf3;
}

.header-container {
  height: 64px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.header-left {
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.collapse-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #dce3ec;
  border-radius: 8px;
  background: #fff;
  color: #3a546d;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.collapse-btn:hover {
  background-color: #f0f4f9;
  border-color: #bdc7d4;
}

.header-breadcrumb {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.crumb-text {
  color: #7f8fa4;
  font-size: 13px;
}

.crumb-text.current {
  color: #2b3f57;
  font-weight: 600;
}

.header-right {
  flex-shrink: 0;
}

.user-info {
  gap: 12px;
  display: flex;
  cursor: pointer;
  position: relative;
  border-radius: 8px;
  align-items: center;
  padding: 6px 12px;
  transition: all 0.2s;
}

.user-info:hover {
  background-color: #f5f7fa;
}

.user-name {
  color: #333;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.dropdown-arrow {
  color: #999;
  font-size: 12px;
  margin-left: 4px;
  transition: transform 0.2s;
}

.user-info:hover .dropdown-arrow {
  transform: rotate(180deg);
}

.user-avatar {
  width: 36px;
  height: 36px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

@media (max-width: 900px) {
  .header-container {
    padding: 0 16px;
  }
}

@media (max-width: 640px) {
  .header-container {
    padding: 0 12px;
  }

  .header-breadcrumb {
    max-width: 150px;
  }
}
</style>
